import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, Typography, Paper, Tabs, Tab, Button, TextField, 
  List, ListItem, ListItemText, ListItemIcon, Divider,
  Card, CardContent, Grid, Switch, FormControlLabel,
  Select, MenuItem, InputLabel, FormControl, Slider,
  Chip, IconButton, CircularProgress, Alert, AlertTitle, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Tooltip, Stack, ListItemSecondaryAction,
  FormGroup, DialogContentText, Checkbox, Avatar
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Psychology as PsychologyIcon,
  Description as DescriptionIcon,
  Message as MessageIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Update as UpdateIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  ContactPhone as ContactPhoneIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  TableChart as CsvIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as ShoppingCartIcon,
  Category as CategoryIcon,
  Store as StoreIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { processFile, generateRAGResponse, KnowledgeFile, updateFile, removeDocument, processProducts } from '../services/ragService';
import { whatsappService, WhatsAppContact, NewLead } from '../services/whatsappService';
import { leadService, Lead } from '../services/leadService';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { Product } from '../redux/slices/productSlice';
import * as mockDataService from '../services/mockData';
import { 
  loadConversationHistory, 
  saveConversationHistory, 
  loadBotStatus, 
  saveBotStatus,
  loadBotSettings,
  saveBotSettings,
  loadAutoLeadCapture,
  saveAutoLeadCapture,
  loadProductIntegration,
  saveProductIntegration,
  BotSettings,
  AutoLeadCaptureSettings,
  Conversation,
  ChatMessage
} from '../services/conversationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ padding: '16px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const WhatsAppAIPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<BotSettings>({
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 150,
    personalityType: 'professional',
    language: 'portuguese',
    autoLearn: true,
    useContext: true,
    useKnowledgeBase: true,
  });

  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [botStatus, setBotStatus] = useState<'stopped' | 'running'>('stopped');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estado para simulação de chat
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
  }>>([]);
  const [userMessage, setUserMessage] = useState('');
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Estados para o diálogo de detalhes do arquivo
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<KnowledgeFile | null>(null);

  // Estados para o diálogo de confirmação de atualização
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [fileToUpdate, setFileToUpdate] = useState<{id: string, file: File, originalName: string} | null>(null);

  // Estados para o diálogo de nova conversa
  const [openNewConversationDialog, setOpenNewConversationDialog] = useState(false);
  const [newConversation, setNewConversation] = useState({
    customerName: '',
    customerPhone: '',
    initialMessage: ''
  });

  const [templates, setTemplates] = useState([
    {
      id: '1',
      name: 'Atendimento Inicial',
      content: 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?',
    },
    {
      id: '2',
      name: 'Qualificação de Lead',
      content: 'Que tipo de imóvel você está procurando? Poderia me dizer seu orçamento?',
    },
  ]);

  const [conversationHistory, setConversationHistory] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);

  const [autoLeadCapture, setAutoLeadCapture] = useState<AutoLeadCaptureSettings>({
    enabled: false,
    autoCreateLead: true,
    captureProfilePic: true,
    notifyNewContacts: true
  });

  const [productIntegrationEnabled, setProductIntegrationEnabled] = useState(false);

  const [replyMessage, setReplyMessage] = useState('');
  const [responseMessage, setResponseMessage] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveSettings = () => {
    setIsLoading(true);
    // Simular salvamento
    setTimeout(() => {
      setIsLoading(false);
      setSnackbar({
        open: true,
        message: 'Configurações salvas com sucesso!',
        severity: 'success',
      });
    }, 1000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Verificar tipo de arquivo
      const allowedTypes = ['application/pdf', 'text/plain', 'text/csv', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type) && 
          !(file.name.endsWith('.csv') || file.name.endsWith('.txt') || file.name.endsWith('.pdf'))) {
        setSnackbar({
          open: true,
          message: 'Formato de arquivo não suportado. Use PDF, TXT ou CSV.',
          severity: 'error',
        });
        return;
      }

      // Verificar tamanho (limite de 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'O arquivo excede o tamanho máximo de 10MB.',
          severity: 'error',
        });
        return;
      }

      // Criar novo arquivo de conhecimento
      const newFile: KnowledgeFile = {
        id: Date.now().toString(),
        name: file.name,
        type: file.type || (file.name.endsWith('.csv') ? 'text/csv' : 
                          file.name.endsWith('.txt') ? 'text/plain' : 'application/pdf'),
        size: file.size,
        uploadDate: new Date(),
        status: 'processing',
        progress: 0,
      };

      setKnowledgeFiles(prev => [...prev, newFile]);
      setIsProcessingFile(true);

      // Processar o arquivo usando o serviço RAG
      processFile(file)
        .then(() => {
          // Atualizar o status do arquivo para pronto
          setKnowledgeFiles(prevFiles => {
            const updatedFiles = [...prevFiles];
            const fileIndex = updatedFiles.findIndex(f => f.id === newFile.id);
            
            if (fileIndex !== -1) {
              updatedFiles[fileIndex] = {
                ...updatedFiles[fileIndex],
                status: 'ready',
                progress: 100,
              };
            }
            
            return updatedFiles;
          });
          
          setIsProcessingFile(false);
          setSnackbar({
            open: true,
            message: `Arquivo "${file.name}" processado com sucesso usando RAG!`,
            severity: 'success',
          });
        })
        .catch(error => {
          // Atualizar o status do arquivo para erro
          setKnowledgeFiles(prevFiles => {
            const updatedFiles = [...prevFiles];
            const fileIndex = updatedFiles.findIndex(f => f.id === newFile.id);
            
            if (fileIndex !== -1) {
              updatedFiles[fileIndex] = {
                ...updatedFiles[fileIndex],
                status: 'error',
                error: error.message,
              };
            }
            
            return updatedFiles;
          });
          
          setIsProcessingFile(false);
          setSnackbar({
            open: true,
            message: `Erro ao processar o arquivo: ${error.message}`,
            severity: 'error',
          });
        });

      // Simulação de progresso durante o processamento
      const interval = setInterval(() => {
        setKnowledgeFiles(prevFiles => {
          const updatedFiles = [...prevFiles];
          const fileIndex = updatedFiles.findIndex(f => f.id === newFile.id);
          
          if (fileIndex !== -1 && updatedFiles[fileIndex].status === 'processing') {
            const currentProgress = updatedFiles[fileIndex].progress || 0;
            if (currentProgress < 90) { // Deixa os últimos 10% para o processamento real
              updatedFiles[fileIndex] = {
                ...updatedFiles[fileIndex],
                progress: currentProgress + 10,
              };
            } else {
              clearInterval(interval);
            }
          } else {
            clearInterval(interval);
          }
          
          return updatedFiles;
        });
      }, 500);
    }

    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Função para lidar com a exclusão de um arquivo
  const handleDeleteFile = (fileId: string) => {
    try {
      // Remover o arquivo da lista
      setKnowledgeFiles(prev => prev.filter(file => file.id !== fileId));
      
      // Remover o documento do serviço RAG
      removeDocument(fileId);
      
      setSnackbar({
        open: true,
        message: 'Arquivo removido com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao remover arquivo:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao remover o arquivo. Por favor, tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleViewFileDetails = (file: KnowledgeFile) => {
    setSelectedFile(file);
    setOpenFileDialog(true);
  };

  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFile(null);
  };

  const handleToggleBot = () => {
    if (botStatus === 'stopped') {
      // Verificar se há arquivos processados
      const hasReadyFiles = knowledgeFiles.some(file => file.status === 'ready');
      
      if (!hasReadyFiles && settings.useKnowledgeBase) {
        setSnackbar({
          open: true,
          message: 'Adicione pelo menos um arquivo de conhecimento ou desative a opção "Usar Base de Conhecimento".',
          severity: 'warning',
        });
        return;
      }

      setBotStatus('running');
      setSnackbar({
        open: true,
        message: 'Bot WhatsApp IA iniciado com sucesso! Usando tecnologia RAG para respostas contextualizadas.',
        severity: 'success',
      });

      // Simulação de uma mensagem recebida após 5 segundos
      if (hasReadyFiles) {
        setTimeout(() => {
          // Exemplo de como o bot usaria RAG para responder
          const exampleQuery = "Quais são as características do imóvel na Rua das Flores?";
          
          setIsLoading(true);
          
          // Gerar resposta usando RAG
          generateRAGResponse(exampleQuery, {
            model: settings.model,
            temperature: settings.temperature,
            personality: settings.personalityType
          })
          .then(response => {
            console.log('Resposta RAG gerada:', response);
            
            // Simulação de estatísticas atualizadas
            setSnackbar({
              open: true,
              message: 'Nova mensagem respondida usando RAG!',
              severity: 'info',
            });
          })
          .catch(error => {
            console.error('Erro ao gerar resposta RAG:', error);
            
            setSnackbar({
              open: true,
              message: 'Erro ao processar mensagem com RAG.',
              severity: 'error',
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
        }, 5000);
      }
    } else {
      setBotStatus('stopped');
      setSnackbar({
        open: true,
        message: 'Bot WhatsApp IA parado com sucesso!',
        severity: 'info',
      });
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PdfIcon color="error" />;
    if (fileType.includes('csv') || fileType.includes('excel')) return <CsvIcon color="success" />;
    return <FileIcon color="primary" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Função para formatar a data em formato legível
  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours} h atrás`;
    if (diffDays === 1) return 'Ontem';
    
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    });
  };

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;
    
    // Adicionar mensagem do usuário ao chat
    const userMessageObj = {
      id: `msg_${Date.now()}`,
      text: userMessage,
      sender: 'user' as const,
      timestamp: new Date(),
    };
    
    setChatMessages(prev => [...prev, userMessageObj]);
    setUserMessage('');
    setIsGeneratingResponse(true);
    
    // Rolar para o final do chat
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    // Verificar se o bot está ativo
    if (botStatus !== 'running') {
      // Adicionar mensagem informando que o bot está inativo
      setTimeout(() => {
        setChatMessages(prev => [
          ...prev,
          {
            id: `msg_${Date.now()}`,
            text: 'O bot está inativo. Por favor, inicie o bot para receber respostas.',
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);
        setIsGeneratingResponse(false);
        
        // Rolar para o final do chat
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }, 1000);
      return;
    }
    
    try {
      // Verificar se a mensagem contém intenções relacionadas a produtos
      if (productIntegrationEnabled) {
        const productIntent = await whatsappService.detectProductIntent(userMessage);
        
        if (productIntent.hasProductIntent) {
          // Processar intenção relacionada a produtos
          let botResponse = '';
          
          if (productIntent.action === 'list') {
            // Listar produtos (possivelmente filtrados por categoria)
            const filteredProducts = productIntent.category 
              ? products.filter(p => p.category?.toLowerCase().includes(productIntent.category || ''))
              : products;
            
            if (filteredProducts.length === 0) {
              botResponse = `Não encontrei produtos${productIntent.category ? ` na categoria ${productIntent.category}` : ''}. Posso ajudar com algo mais?`;
            } else {
              botResponse = `Encontrei ${filteredProducts.length} produtos${productIntent.category ? ` na categoria ${productIntent.category}` : ''}:\n\n`;
              
              filteredProducts.slice(0, 5).forEach((product, index) => {
                botResponse += `${index + 1}. **${product.name}** - R$ ${product.price.toFixed(2)}\n`;
              });
              
              if (filteredProducts.length > 5) {
                botResponse += `\n... e mais ${filteredProducts.length - 5} produtos. Gostaria de informações sobre algum deles?`;
              } else {
                botResponse += `\nGostaria de informações detalhadas sobre algum desses produtos?`;
              }
            }
          } else if (productIntent.action === 'info' || productIntent.action === 'price') {
            // Buscar informações sobre um produto específico
            // Tentativa simples de encontrar um produto pelo nome na mensagem
            const productMatch = products.find(p => 
              userMessage.toLowerCase().includes(p.name.toLowerCase())
            );
            
            if (productMatch) {
              botResponse = `**${productMatch.name}**\n\n${productMatch.description}\n\n`;
              botResponse += `**Preço:** R$ ${productMatch.price.toFixed(2)}\n`;
              botResponse += `**Categoria:** ${productMatch.category || 'Não categorizado'}\n`;
              botResponse += `**Tipo:** ${productMatch.type === 'produto' ? 'Produto' : 'Serviço'}\n\n`;
              botResponse += `Posso enviar mais informações ou agendar uma demonstração deste ${productMatch.type}. Gostaria de prosseguir?`;
            } else {
              // Não encontrou produto específico
              botResponse = `Não consegui identificar um produto específico na sua mensagem. Temos ${products.length} produtos disponíveis. Gostaria de ver a lista completa?`;
            }
          } else if (productIntent.action === 'availability') {
            // Verificar disponibilidade
            const productMatch = products.find(p => 
              userMessage.toLowerCase().includes(p.name.toLowerCase())
            );
            
            if (productMatch) {
              botResponse = `O produto **${productMatch.name}** está disponível para compra imediata. Gostaria de receber mais informações ou prosseguir com a aquisição?`;
            } else {
              botResponse = `Todos os nossos produtos estão disponíveis para compra imediata. Gostaria de informações sobre algum produto específico?`;
            }
          } else {
            // Intenção genérica relacionada a produtos
            botResponse = `Temos ${products.length} produtos e serviços disponíveis em nosso catálogo. Gostaria de ver a lista completa ou buscar por uma categoria específica?`;
          }
          
          // Adicionar resposta do bot ao chat
          setChatMessages(prev => [
            ...prev,
            {
              id: `msg_${Date.now()}`,
              text: botResponse,
              sender: 'bot',
              timestamp: new Date(),
            },
          ]);
          
          setIsGeneratingResponse(false);
          return;
        }
      }
      
      // Se não for relacionado a produtos ou a integração estiver desativada, usar RAG
      const response = await generateRAGResponse(userMessage, {
        model: settings.model,
        temperature: settings.temperature,
        personality: settings.personalityType
      });
      
      // Adicionar resposta do bot ao chat
      setChatMessages(prev => [
        ...prev,
        {
          id: `msg_${Date.now()}`,
          text: response,
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error('Erro ao gerar resposta:', error);
      
      // Adicionar mensagem de erro ao chat
      setChatMessages(prev => [
        ...prev,
        {
          id: `msg_${Date.now()}`,
          text: 'Ocorreu um erro ao gerar a resposta. Por favor, tente novamente.',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsGeneratingResponse(false);
      
      // Rolar para o final do chat
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Função para abrir o diálogo de confirmação de atualização
  const handleUpdateFileClick = (file: KnowledgeFile) => {
    // Abrir diálogo para selecionar novo arquivo
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.txt,.csv';
    fileInput.onchange = () => {
      if (fileInput.files && fileInput.files.length > 0) {
        // Armazenar o arquivo selecionado e abrir diálogo de confirmação
        setFileToUpdate({
          id: file.id,
          file: fileInput.files[0],
          originalName: file.name
        });
        setOpenConfirmDialog(true);
      }
    };
    fileInput.click();
  };

  // Função para confirmar a atualização do arquivo
  const handleConfirmUpdate = () => {
    if (fileToUpdate) {
      handleUpdateFile(fileToUpdate.id, fileToUpdate.file);
      setOpenConfirmDialog(false);
      setFileToUpdate(null);
    }
  };

  const handleUpdateFile = async (fileId: string, newFile: File) => {
    try {
      setIsProcessingFile(true);
      
      // Atualizar o status do arquivo para "processing"
      setKnowledgeFiles(prev => 
        prev.map(file => 
          file.id === fileId 
            ? { ...file, status: 'processing' as const, progress: 0 } 
            : file
        )
      );
      
      // Chamar o serviço para atualizar o arquivo
      const updatedFile = await updateFile(fileId, newFile);
      
      // Atualizar a lista de arquivos
      setKnowledgeFiles(prev => 
        prev.map(file => 
          file.id === fileId ? updatedFile : file
        )
      );
      
      setSnackbar({
        open: true,
        message: 'Arquivo atualizado com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao atualizar arquivo:', error);
      
      // Atualizar o status do arquivo para "error"
      setKnowledgeFiles(prev => 
        prev.map(file => 
          file.id === fileId 
            ? { ...file, status: 'error' as const, error: 'Erro ao atualizar arquivo.' } 
            : file
        )
      );
      
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar o arquivo. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsProcessingFile(false);
    }
  };

  // Função para adicionar uma nova conversa
  const handleAddNewConversation = async () => {
    if (!newConversation.customerName || !newConversation.customerPhone) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha o nome e telefone do cliente',
        severity: 'error'
      });
      return;
    }

    try {
      // Criar nova conversa
      const initialMessage = newConversation.initialMessage || 'Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?';
      
      const newConv: Conversation = {
        id: Date.now().toString(),
        customerName: newConversation.customerName,
        customerPhone: newConversation.customerPhone,
        lastMessage: initialMessage,
        timestamp: new Date(),
        messages: [
          {
            id: Date.now().toString(),
            text: initialMessage,
            sender: 'bot',
            timestamp: new Date()
          }
        ],
        status: 'active'
      };
      
      // Atualizar o histórico de conversas
      const updatedHistory = [...conversationHistory, newConv];
      setConversationHistory(updatedHistory);
      saveConversationHistory(updatedHistory);
      
      // Selecionar a nova conversa
      setSelectedConversation(newConv.id);
      
      // Limpar o formulário
      setNewConversation({
        customerName: '',
        customerPhone: '',
        initialMessage: ''
      });
      
      // Fechar o diálogo
      setOpenNewConversationDialog(false);
      
      // Enviar mensagem via WhatsApp (simulação)
      try {
        await whatsappService.sendMessage(newConversation.customerPhone, initialMessage);
        setSnackbar({
          open: true,
          message: 'Conversa iniciada com sucesso!',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erro ao enviar mensagem via WhatsApp:', error);
        setSnackbar({
          open: true,
          message: 'Conversa criada, mas houve um erro ao enviar a mensagem inicial via WhatsApp',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Erro ao criar nova conversa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar nova conversa',
        severity: 'error'
      });
    }
  };

  // Função para responder a uma conversa selecionada
  const handleRespondToConversation = () => {
    const conversation = conversationHistory.find(c => c.id === selectedConversation);
    if (!conversation || conversation.status === 'closed' || !botStatus || botStatus === 'stopped') return;
    
    // Abrir o diálogo de resposta
    setOpenResponseDialog(true);
  };

  // Estados para o diálogo de resposta
  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [isGeneratingConversationResponse, setIsGeneratingConversationResponse] = useState(false);

  // Função para enviar resposta a uma conversa
  const handleSendResponse = async () => {
    if (!responseMessage.trim() || !selectedConversation) return;
    
    setIsGeneratingConversationResponse(true);
    
    try {
      // Gerar resposta usando RAG
      const response = await generateRAGResponse(responseMessage, {
        model: settings.model,
        temperature: settings.temperature,
        personality: settings.personalityType
      });
      
      // Criar mensagens
      const userMessageObj = {
        id: `msg_${Date.now()}`,
        text: responseMessage,
        sender: 'user' as const,
        timestamp: new Date(),
      };
      
      const botMessageObj = {
        id: `msg_${Date.now() + 1}`,
        text: response,
        sender: 'bot' as const,
        timestamp: new Date(Date.now() + 2000), // 2 segundos depois
      };
      
      // Atualizar a conversa
      setConversationHistory(prev => 
        prev.map(c => {
          if (c.id === selectedConversation) {
            return {
              ...c,
              lastMessage: response,
              timestamp: botMessageObj.timestamp,
              messages: [...c.messages, userMessageObj, botMessageObj]
            };
          }
          return c;
        })
      );
      
      // Fechar o diálogo e limpar o campo
      setOpenResponseDialog(false);
      setResponseMessage('');
      
      // Mostrar mensagem de sucesso
      setSnackbar({
        open: true,
        message: 'Resposta enviada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao gerar resposta:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao gerar resposta. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsGeneratingConversationResponse(false);
    }
  };

  // Estados para gerenciamento de contatos do WhatsApp e leads
  const [whatsappContacts, setWhatsappContacts] = useState<WhatsAppContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<WhatsAppContact | null>(null);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);

  // Estados para produtos
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>('');
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [phoneNumberForProduct, setPhoneNumberForProduct] = useState('');
  const [isSendingProduct, setIsSendingProduct] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isProcessingProducts, setIsProcessingProducts] = useState(false);
  const [processedProductsCount, setProcessedProductsCount] = useState(0);
  const [selectedProductsForKnowledge, setSelectedProductsForKnowledge] = useState<string[]>([]);
  const [openKnowledgeDialog, setOpenKnowledgeDialog] = useState(false);
  const [productsInKnowledgeBase, setProductsInKnowledgeBase] = useState<string[]>([]);
  const [isRemovingFromKnowledge, setIsRemovingFromKnowledge] = useState(false);

  // Notificações
  const notify = useCreateNotification();

  const handleSendProductInfo = async () => {
    if (!selectedProduct || !phoneNumberForProduct) return;
    
    setIsSendingProduct(true);
    
    try {
      // Enviar informações do produto via WhatsApp
      await whatsappService.sendProductInfo(phoneNumberForProduct, selectedProduct);
      
      // Criar notificação
      notify({
        title: 'Produto Enviado',
        message: `Informações do produto "${selectedProduct.name}" foram enviadas com sucesso para ${phoneNumberForProduct}`,
        type: 'success',
      });
      
      setSnackbar({
        open: true,
        message: 'Informações do produto enviadas com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao enviar informações do produto:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao enviar informações do produto. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsSendingProduct(false);
      setProductDialogOpen(false);
    }
  };

  // Função para carregar produtos do sistema principal
  const handleLoadProducts = async () => {
    setIsLoadingProducts(true);
    
    try {
      const productsData = await mockDataService.getProducts();
      setProducts(productsData);
      
      // Extrair categorias únicas dos produtos
      const categories = Array.from(new Set(productsData
        .map((p: Product) => p.category)
        .filter(Boolean) as string[]
      ));
      
      setProductCategories(categories);
      
      notify({
        title: 'Produtos Carregados',
        message: `${productsData.length} produtos carregados com sucesso.`,
        type: 'success',
      });
      
      setSnackbar({
        open: true,
        message: `${productsData.length} produtos carregados com sucesso.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao carregar produtos. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Carregar produtos apenas quando a integração de produtos for ativada
  useEffect(() => {
    if (productIntegrationEnabled && products.length === 0) {
      handleLoadProducts();
    }
  }, [productIntegrationEnabled, products.length]);

  useEffect(() => {
    // Verificar quais produtos já estão na base de conhecimento
    const productsInKnowledge = knowledgeFiles
      .filter(file => file.type === 'product')
      .map(file => {
        // Extrair ID do produto do nome do arquivo (formato: "Produto: Nome do Produto")
        const productName = file.name.replace('Produto: ', '');
        const product = products.find(p => p.name === productName);
        return product?.id;
      })
      .filter(Boolean) as string[];
    
    setProductsInKnowledgeBase(productsInKnowledge);
  }, [knowledgeFiles, products]);

  // Efeito para atualizar as categorias de produtos quando houver produtos carregados
  useEffect(() => {
    if (products.length > 0) {
      // Extrair categorias únicas dos produtos já carregados
      const categories = Array.from(new Set(products
        .map((p: Product) => p.category)
        .filter(Boolean) as string[]
      ));
      
      setProductCategories(categories);
    }
  }, [products]);

  // Função para processar produtos e adicioná-los à base de conhecimento
  const handleProcessProductsForKnowledgeBase = async () => {
    if (products.length === 0) {
      setSnackbar({
        open: true,
        message: 'Não há produtos para processar. Carregue produtos primeiro.',
        severity: 'warning',
      });
      return;
    }
    
    if (selectedProductsForKnowledge.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um produto para adicionar à base de conhecimento.',
        severity: 'warning',
      });
      return;
    }
    
    setIsProcessingProducts(true);
    setProcessedProductsCount(0);
    
    try {
      // Filtrar apenas os produtos selecionados
      const productsToProcess = products.filter(p => selectedProductsForKnowledge.includes(p.id));
      
      // Processar produtos e adicioná-los à base de conhecimento
      const knowledgeFiles = await processProducts(productsToProcess);
      
      setProcessedProductsCount(knowledgeFiles.length);
      
      // Atualizar a lista de arquivos de conhecimento
      setKnowledgeFiles(prev => [...prev, ...knowledgeFiles]);
      
      // Atualizar a lista de produtos na base de conhecimento
      setProductsInKnowledgeBase(prev => [...prev, ...productsToProcess.map(p => p.id)]);
      
      // Limpar seleção
      setSelectedProductsForKnowledge([]);
      
      notify({
        title: 'Produtos Processados',
        message: `${knowledgeFiles.length} produtos foram adicionados à base de conhecimento.`,
        type: 'success',
      });
      
      setSnackbar({
        open: true,
        message: `${knowledgeFiles.length} produtos foram adicionados à base de conhecimento.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao processar produtos para base de conhecimento:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao processar produtos. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsProcessingProducts(false);
      setOpenKnowledgeDialog(false);
    }
  };
  
  // Função para remover produtos da base de conhecimento
  const handleRemoveProductsFromKnowledgeBase = async () => {
    if (selectedProductsForKnowledge.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um produto para remover da base de conhecimento.',
        severity: 'warning',
      });
      return;
    }
    
    setIsRemovingFromKnowledge(true);
    
    try {
      // Remover documentos relacionados a produtos da base de conhecimento
      for (const productId of selectedProductsForKnowledge) {
        // Encontrar documentos relacionados a este produto
        const documentsToRemove = knowledgeFiles
          .filter(file => file.name.includes(`Produto: ${products.find(p => p.id === productId)?.name}`))
          .map(file => file.id);
        
        // Remover cada documento
        for (const docId of documentsToRemove) {
          await removeDocument(docId);
        }
      }
      
      // Atualizar a lista de arquivos de conhecimento
      setKnowledgeFiles(prev => 
        prev.filter(file => !selectedProductsForKnowledge.some(productId => 
          file.name.includes(`Produto: ${products.find(p => p.id === productId)?.name}`)
        ))
      );
      
      // Atualizar a lista de produtos na base de conhecimento
      setProductsInKnowledgeBase(prev => 
        prev.filter(id => !selectedProductsForKnowledge.includes(id))
      );
      
      // Limpar seleção
      setSelectedProductsForKnowledge([]);
      
      notify({
        title: 'Produtos Removidos',
        message: `${selectedProductsForKnowledge.length} produtos foram removidos da base de conhecimento.`,
        type: 'success',
      });
      
      setSnackbar({
        open: true,
        message: `${selectedProductsForKnowledge.length} produtos foram removidos da base de conhecimento.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao remover produtos da base de conhecimento:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao remover produtos. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsRemovingFromKnowledge(false);
      setOpenKnowledgeDialog(false);
    }
  };
  
  // Função para selecionar/desselecionar todos os produtos
  const handleToggleSelectAllProducts = () => {
    if (selectedProductsForKnowledge.length === products.length) {
      // Se todos estão selecionados, desselecionar todos
      setSelectedProductsForKnowledge([]);
    } else {
      // Caso contrário, selecionar todos
      setSelectedProductsForKnowledge(products.map(p => p.id));
    }
  };

  useEffect(() => {
    // Carregar histórico de conversas e status do bot ao iniciar
    const savedConversations = loadConversationHistory();
    if (savedConversations.length > 0) {
      setConversationHistory(savedConversations);
    }
    
    const savedBotStatus = loadBotStatus();
    setBotStatus(savedBotStatus.status);
    
    const savedBotSettings = loadBotSettings();
    setSettings(savedBotSettings);
    
    const savedAutoLeadCapture = loadAutoLeadCapture();
    setAutoLeadCapture(savedAutoLeadCapture);
    
    // Sempre iniciar com a integração de produtos desligada
    setProductIntegrationEnabled(false);
  }, []);

  useEffect(() => {
    // Salvar histórico de conversas quando for atualizado
    saveConversationHistory(conversationHistory);
  }, [conversationHistory]);

  useEffect(() => {
    // Salvar status do bot quando for atualizado
    saveBotStatus(botStatus);
  }, [botStatus]);

  useEffect(() => {
    // Salvar configurações do bot quando forem atualizadas
    saveBotSettings(settings);
  }, [settings]);

  useEffect(() => {
    // Salvar configuração de captura automática quando for atualizada
    saveAutoLeadCapture(autoLeadCapture);
  }, [autoLeadCapture]);

  useEffect(() => {
    // Salvar integração de produtos quando for atualizada
    saveProductIntegration(productIntegrationEnabled);
  }, [productIntegrationEnabled]);

  // Função para atualizar configuração de captura automática
  const handleUpdateAutoLeadCapture = async () => {
    try {
      await whatsappService.configureAutoLeadCapture(
        autoLeadCapture.enabled,
        {
          autoCreateLead: autoLeadCapture.autoCreateLead,
          captureProfilePic: autoLeadCapture.captureProfilePic,
          notifyNewContacts: autoLeadCapture.notifyNewContacts
        }
      );
      
      setSnackbar({
        open: true,
        message: 'Configuração de captura automática atualizada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao atualizar configuração de captura automática:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar configuração. Por favor, tente novamente.',
        severity: 'error',
      });
    }
  };

  // Função para carregar contatos recentes do WhatsApp
  const handleLoadWhatsAppContacts = async () => {
    setIsLoadingContacts(true);
    
    try {
      const contacts = await whatsappService.getRecentContacts(50);
      setWhatsappContacts(contacts);
      
      setSnackbar({
        open: true,
        message: `${contacts.length} contatos do WhatsApp carregados com sucesso!`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao carregar contatos do WhatsApp:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao carregar contatos do WhatsApp. Verifique a conexão.',
        severity: 'error',
      });
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // Função para abrir o diálogo de detalhes do contato
  const handleOpenContactDetails = (contact: WhatsAppContact) => {
    setSelectedContact(contact);
    setOpenContactDialog(true);
  };

  // Função para criar um lead a partir de um contato do WhatsApp
  const handleCreateLeadFromContact = async () => {
    if (!selectedContact) return;
    
    setIsCreatingLead(true);
    
    try {
      // Criar lead a partir do contato
      const newLead = await whatsappService.createLeadFromWhatsAppContact(
        selectedContact.phoneNumber,
        'Importação automática WhatsApp'
      );
      
      // Salvar lead no sistema
      const createdLead = await leadService.createLeadFromWhatsApp(newLead);
      
      setSnackbar({
        open: true,
        message: `Lead "${createdLead.name}" criado com sucesso!`,
        severity: 'success',
      });
      
      // Fechar o diálogo
      setOpenContactDialog(false);
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao criar lead. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsCreatingLead(false);
    }
  };

  // Função para criar leads em massa a partir de contatos selecionados
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [isCreatingBulkLeads, setIsCreatingBulkLeads] = useState(false);

  const handleToggleContactSelection = (contactId: string) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleCreateBulkLeads = async () => {
    if (selectedContacts.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um contato para criar leads.',
        severity: 'warning',
      });
      return;
    }
    
    setIsCreatingBulkLeads(true);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      // Processar cada contato selecionado
      for (const contactId of selectedContacts) {
        const contact = whatsappContacts.find(c => c.id === contactId);
        
        if (contact) {
          try {
            const newLead = await whatsappService.createLeadFromWhatsAppContact(
              contact.phoneNumber,
              'Importação em massa WhatsApp'
            );
            
            await leadService.createLeadFromWhatsApp(newLead);
            successCount++;
          } catch (error) {
            console.error(`Erro ao criar lead para ${contact.name}:`, error);
            errorCount++;
          }
        }
      }
      
      // Limpar seleção
      setSelectedContacts([]);
      
      setSnackbar({
        open: true,
        message: `${successCount} leads criados com sucesso! ${errorCount > 0 ? `(${errorCount} falhas)` : ''}`,
        severity: errorCount > 0 ? 'warning' : 'success',
      });
    } catch (error) {
      console.error('Erro ao criar leads em massa:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao criar leads em massa. Por favor, tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsCreatingBulkLeads(false);
    }
  };

  // Função para formatar o tempo relativo (ex: "5 min atrás")
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'agora mesmo';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min atrás`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} h atrás`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'dia' : 'dias'} atrás`;
    }
  };

  // Função para enviar resposta a uma conversa
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;
    
    try {
      setIsGeneratingResponse(true);
      
      // Encontrar a conversa selecionada
      const conversation = conversationHistory.find(c => c.id === selectedConversation);
      if (!conversation) return;
      
      // Adicionar mensagem do bot à conversa
      const botMessage: ChatMessage = {
        id: Date.now().toString(),
        text: replyMessage,
        sender: 'bot',
        timestamp: new Date()
      };
      
      // Atualizar a conversa com a nova mensagem
      const updatedConversation: Conversation = {
        ...conversation,
        lastMessage: replyMessage,
        timestamp: new Date(),
        messages: [...conversation.messages, botMessage]
      };
      
      // Atualizar o histórico de conversas
      const updatedHistory = conversationHistory.map(c => 
        c.id === selectedConversation ? updatedConversation : c
      );
      
      setConversationHistory(updatedHistory);
      saveConversationHistory(updatedHistory);
      
      // Limpar o campo de resposta
      setReplyMessage('');
      
      // Enviar mensagem via WhatsApp (simulação)
      try {
        await whatsappService.sendMessage(conversation.customerPhone, replyMessage);
        setSnackbar({
          open: true,
          message: 'Mensagem enviada com sucesso!',
          severity: 'success'
        });
      } catch (error) {
        console.error('Erro ao enviar mensagem via WhatsApp:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao enviar mensagem via WhatsApp',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
      
      setSnackbar({
        open: true,
        message: 'Erro ao enviar resposta',
        severity: 'error'
      });
    } finally {
      setIsGeneratingResponse(false);
      
      // Rolar para o final do chat
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Função para alternar o status da conversa (ativa/encerrada)
  const handleToggleConversationStatus = (conversationId: string) => {
    const conversation = conversationHistory.find(c => c.id === conversationId);
    if (!conversation) return;
    
    const newStatus = conversation.status === 'active' ? 'closed' : 'active';
    
    // Atualizar o status da conversa
    const updatedHistory = conversationHistory.map(c => 
      c.id === conversationId ? { ...c, status: newStatus as 'active' | 'closed' } : c
    );
    
    setConversationHistory(updatedHistory);
    saveConversationHistory(updatedHistory);
    
    setSnackbar({
      open: true,
      message: `Conversa ${newStatus === 'active' ? 'reaberta' : 'encerrada'} com sucesso!`,
      severity: 'success'
    });
  };

  // Função para excluir uma conversa
  const handleDeleteConversation = (conversationId: string) => {
    // Confirmar exclusão
    if (!window.confirm('Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    // Remover a conversa do histórico
    const updatedHistory = conversationHistory.filter(c => c.id !== conversationId);
    
    // Se a conversa excluída estiver selecionada, limpar a seleção
    if (selectedConversation === conversationId) {
      setSelectedConversation('');
    }
    
    setConversationHistory(updatedHistory);
    saveConversationHistory(updatedHistory);
    
    // Mostrar mensagem de sucesso
    setSnackbar({
      open: true,
      message: 'Conversa excluída com sucesso!',
      severity: 'success'
    });
  };

  // Função para alternar a integração de produtos
  const handleToggleProductIntegration = () => {
    const newValue = !productIntegrationEnabled;
    setProductIntegrationEnabled(newValue);
    
    // Se estiver ativando a integração e não houver produtos carregados, carregá-los
    if (newValue && products.length === 0) {
      handleLoadProducts();
    }
    
    setSnackbar({
      open: true,
      message: `Integração de produtos ${newValue ? 'ativada' : 'desativada'} com sucesso!`,
      severity: 'success',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon fontSize="large" />
        WhatsApp IA Bot
      </Typography>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2 
        }}>
          <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon color="primary" />
            WhatsApp IA Bot
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/conversas')}
            startIcon={<MessageIcon />}
          >
            Gerenciar Conversas
          </Button>
        </Box>
        
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<StorageIcon />} label="Base de Conhecimento" />
          <Tab icon={<SettingsIcon />} label="Configurações" />
          <Tab icon={<AssessmentIcon />} label="Status e Estatísticas" />
          <Tab icon={<PersonAddIcon />} label="Captura de Leads" />
          <Tab icon={<ShoppingCartIcon />} label="Produtos e Serviços" />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Explicação RAG */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon />
                Tecnologia RAG (Retrieval-Augmented Generation)
              </Typography>
              
              <Typography variant="body2" paragraph>
                O WhatsApp IA Bot utiliza a tecnologia RAG (Retrieval-Augmented Generation) para fornecer respostas precisas e contextualizadas com base nos documentos carregados.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      1. Busca Inteligente
                    </Typography>
                    <Typography variant="body2">
                      O sistema analisa os documentos carregados e extrai as informações mais relevantes para cada pergunta.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      2. Contextualização
                    </Typography>
                    <Typography variant="body2">
                      As respostas são geradas considerando o contexto específico encontrado nos documentos, garantindo precisão.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      3. Respostas Naturais
                    </Typography>
                    <Typography variant="body2">
                      A IA formata as informações em respostas naturais e fluidas, como se fossem escritas por um humano.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Upload de Arquivos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUploadIcon />
                Base de Conhecimento
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Carregue arquivos (PDF, TXT, CSV) que serão usados como base de conhecimento para o bot. 
                Os arquivos serão processados e utilizados para gerar respostas contextualizadas.
                Você pode atualizar os arquivos quando necessário para manter a base de conhecimento sempre atualizada.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Faça upload de arquivos PDF, TXT ou CSV para que o bot possa usar como base de conhecimento para responder às mensagens.
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessingFile}
                  >
                    Carregar Arquivo
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    accept=".pdf,.txt,.csv"
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    Formatos suportados: PDF, TXT, CSV (máx. 10MB)
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Lista de Arquivos */}
              <Typography variant="subtitle1" gutterBottom>
                Arquivos Carregados
              </Typography>

              {knowledgeFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Nenhum arquivo carregado. Adicione arquivos para que o bot possa usá-los como base de conhecimento.
                </Typography>
              ) : (
                <List>
                  {knowledgeFiles.map((file) => (
                    <ListItem
                      key={file.id}
                      secondaryAction={
                        <ListItemSecondaryAction>
                          {file.status === 'processing' ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <Tooltip title="Atualizar arquivo">
                                <IconButton 
                                  edge="end" 
                                  aria-label="update"
                                  onClick={() => handleUpdateFileClick(file)}
                                  disabled={isProcessingFile}
                                >
                                  <RefreshIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir arquivo">
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => handleDeleteFile(file.id)}
                                  disabled={isProcessingFile}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </ListItemSecondaryAction>
                      }
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemIcon>
                        {getFileIcon(file.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {file.name}
                            <Chip
                              label={file.status === 'ready' ? 'Pronto' : file.status === 'processing' ? 'Processando' : 'Erro'}
                              color={file.status === 'ready' ? 'success' : file.status === 'processing' ? 'warning' : 'error'}
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {formatFileSize(file.size)} • {formatDate(file.uploadDate)}
                            </Typography>
                            
                            {file.status === 'processing' && (
                              <LinearProgress 
                                variant="determinate" 
                                value={file.progress || 0} 
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                            )}
                          </>
                        }
                      />
                      
                      <Tooltip title="Ver detalhes">
                        <IconButton onClick={() => handleViewFileDetails(file)}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          {/* Configurações do Modelo */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                Configurações do Modelo
              </Typography>
              
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Modelo de IA</InputLabel>
                  <Select
                    value={settings.model}
                    label="Modelo de IA"
                    onChange={(e) => setSettings({ ...settings, model: e.target.value })}
                  >
                    <MenuItem value="gpt-4">GPT-4 (Mais avançado)</MenuItem>
                    <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Mais rápido)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Personalidade</InputLabel>
                  <Select
                    value={settings.personalityType}
                    label="Personalidade"
                    onChange={(e) => setSettings({ ...settings, personalityType: e.target.value })}
                  >
                    <MenuItem value="professional">Profissional</MenuItem>
                    <MenuItem value="friendly">Amigável</MenuItem>
                    <MenuItem value="expert">Especialista</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoLearn}
                      onChange={(e) => setSettings({ ...settings, autoLearn: e.target.checked })}
                    />
                  }
                  label="Aprendizado Automático"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.useContext}
                      onChange={(e) => setSettings({ ...settings, useContext: e.target.checked })}
                    />
                  }
                  label="Usar Contexto da Conversa"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.useKnowledgeBase}
                      onChange={(e) => setSettings({ ...settings, useKnowledgeBase: e.target.checked })}
                    />
                  }
                  label="Usar Base de Conhecimento"
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Templates de Mensagem */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon />
                Templates de Mensagem
              </Typography>

              <Stack spacing={2}>
                {templates.map((template) => (
                  <Card key={template.id} variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {template.name}
                        <Chip
                          size="small"
                          label="IA"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {template.content}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  fullWidth
                >
                  Gerar Novo Template com IA
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Botões de Ação */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Salvar Configurações'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          {/* Configuração do Bot */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                Configuração do Bot
              </Typography>
              
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={botStatus === 'running'}
                      onChange={(e) => handleToggleBot()}
                    />
                  }
                  label="Ativar Bot"
                />
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Ative o bot para responder automaticamente às mensagens.
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* Status do Bot */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon />
                Status do Bot
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: botStatus === 'running' ? 'success.main' : 'error.main',
                    color: 'white',
                    mb: 2,
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <Typography variant="h5">
                    {botStatus === 'running' ? 'ATIVO' : 'INATIVO'}
                  </Typography>
                </Box>
                
                <Typography variant="body1" gutterBottom>
                  {botStatus === 'running' 
                    ? 'O bot está ativo e respondendo mensagens automaticamente.' 
                    : 'O bot está inativo. Clique em "Iniciar Bot" para ativá-lo.'}
                </Typography>
                
                <Button
                  variant="contained"
                  color={botStatus === 'running' ? 'error' : 'success'}
                  startIcon={botStatus === 'running' ? <StopIcon /> : <PlayArrowIcon />}
                  onClick={handleToggleBot}
                  sx={{ mt: 2 }}
                >
                  {botStatus === 'running' ? 'Parar Bot' : 'Iniciar Bot'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Estatísticas */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon />
                Estatísticas
              </Typography>
              
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h4">152</Typography>
                    <Typography variant="body2">Mensagens Recebidas</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'success.light',
                      color: 'success.contrastText',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h4">147</Typography>
                    <Typography variant="body2">Mensagens Respondidas</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h4">98%</Typography>
                    <Typography variant="body2">Taxa de Resposta</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h4">23</Typography>
                    <Typography variant="body2">Novos Leads Gerados</Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Desempenho Semanal
                </Typography>
                <Box sx={{ height: 300, width: '100%' }}>
                  {/* Aqui seria inserido um componente de gráfico */}
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Gráfico de desempenho semanal
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Tópicos Mais Frequentes
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Chip label="Preços (32)" color="primary" />
                  <Chip label="Localização (27)" color="primary" />
                  <Chip label="Financiamento (19)" color="primary" />
                  <Chip label="Visitas (15)" color="primary" />
                  <Chip label="Documentação (12)" color="primary" />
                  <Chip label="Condomínio (8)" color="primary" />
                  <Chip label="Reforma (5)" color="primary" />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />}>
                  Exportar Relatório
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          {/* Configuração de Captura Automática */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon />
                Captura Automática de Leads
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure a captura automática de informações de contatos do WhatsApp para criar leads no sistema.
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoLeadCapture.enabled}
                      onChange={(e) => setAutoLeadCapture(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                  }
                  label="Ativar captura automática de leads"
                />
                
                <Box sx={{ ml: 4, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoLeadCapture.autoCreateLead}
                        onChange={(e) => setAutoLeadCapture(prev => ({ ...prev, autoCreateLead: e.target.checked }))}
                        disabled={!autoLeadCapture.enabled}
                      />
                    }
                    label="Criar leads automaticamente"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoLeadCapture.captureProfilePic}
                        onChange={(e) => setAutoLeadCapture(prev => ({ ...prev, captureProfilePic: e.target.checked }))}
                        disabled={!autoLeadCapture.enabled}
                      />
                    }
                    label="Capturar foto de perfil"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoLeadCapture.notifyNewContacts}
                        onChange={(e) => setAutoLeadCapture(prev => ({ ...prev, notifyNewContacts: e.target.checked }))}
                        disabled={!autoLeadCapture.enabled}
                      />
                    }
                    label="Notificar sobre novos contatos"
                  />
                </Box>
              </FormGroup>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleUpdateAutoLeadCapture}
                >
                  Salvar Configurações
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Contatos do WhatsApp */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContactPhoneIcon />
                Contatos do WhatsApp
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  {selectedContacts.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={handleCreateBulkLeads}
                      disabled={isCreatingBulkLeads}
                      sx={{ mr: 1 }}
                    >
                      {isCreatingBulkLeads ? (
                        <CircularProgress size={24} />
                      ) : (
                        `Criar ${selectedContacts.length} Lead(s)`
                      )}
                    </Button>
                  )}
                  
                  <Button
                    component={RouterLink}
                    to="/leads"
                    variant="outlined"
                    color="secondary"
                    startIcon={<PersonIcon />}
                    sx={{ mr: 1 }}
                  >
                    Ver Todos os Leads
                  </Button>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleLoadWhatsAppContacts}
                  disabled={isLoadingContacts}
                >
                  {isLoadingContacts ? <CircularProgress size={24} /> : 'Atualizar Contatos'}
                </Button>
              </Box>
              
              {whatsappContacts.length === 0 ? (
                <Box sx={{ 
                  p: 4, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  color: 'text.secondary',
                  bgcolor: 'background.default',
                  borderRadius: 1
                }}>
                  <ContactPhoneIcon sx={{ fontSize: 48 }} />
                  <Typography>
                    Nenhum contato carregado. Clique em "Atualizar Contatos" para carregar os contatos do WhatsApp.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {whatsappContacts.map((contact) => (
                    <ListItem
                      key={contact.id}
                      button
                      onClick={() => handleOpenContactDetails(contact)}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleContactSelection(contact.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <ListItemIcon>
                        {contact.profilePicUrl ? (
                          <Box
                            component="img"
                            src={contact.profilePicUrl}
                            alt={contact.name}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <PersonIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" noWrap sx={{ maxWidth: '70%' }}>
                              {contact.name}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={contact.phoneNumber} 
                              color="primary"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {contact.lastMessage || contact.about || 'Sem mensagens recentes'}
                          </Typography>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Tooltip title="Criar Lead">
                          <IconButton 
                            edge="end" 
                            aria-label="create-lead"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContact(contact);
                              handleCreateLeadFromContact();
                            }}
                            disabled={isCreatingLead}
                          >
                            {isCreatingLead && selectedContact?.id === contact.id ? (
                              <CircularProgress size={24} />
                            ) : (
                              <PersonAddIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Grid container spacing={3}>
          {/* Configuração de Produtos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon />
                Configuração de Produtos
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure a integração de produtos no WhatsApp IA Bot.
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productIntegrationEnabled}
                      onChange={(e) => handleToggleProductIntegration()}
                    />
                  }
                  label="Ativar integração de produtos"
                />
              </FormGroup>
              
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>Base de Conhecimento e Produtos</AlertTitle>
                Ao processar produtos para a base de conhecimento, o bot poderá fornecer informações mais detalhadas e precisas sobre os produtos quando os clientes fizerem perguntas. Isso melhora significativamente a qualidade das respostas automáticas.
                {processedProductsCount > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{processedProductsCount}</strong> produtos foram processados e adicionados à base de conhecimento.
                  </Typography>
                )}
              </Alert>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<StoreIcon />}
                  onClick={handleLoadProducts}
                  disabled={isLoadingProducts}
                >
                  {isLoadingProducts ? <CircularProgress size={24} /> : 'Buscar Produtos do Sistema'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setOpenKnowledgeDialog(true)}
                  disabled={isLoadingProducts || isProcessingProducts}
                  sx={{ ml: 2 }}
                >
                  Adicionar Produtos à Base de Conhecimento
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Produtos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon />
                Produtos ({products.length})
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Filtrar por Categoria</InputLabel>
                  <Select
                    value={selectedProductCategory}
                    onChange={(e) => setSelectedProductCategory(e.target.value)}
                    label="Filtrar por Categoria"
                  >
                    <MenuItem value="">Todas as Categorias</MenuItem>
                    {productCategories.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setProductDialogOpen(true)}
                  disabled={products.length === 0}
                >
                  Enviar Produto via WhatsApp
                </Button>
              </Box>
              
              {products.length === 0 ? (
                <Box sx={{ 
                  p: 4, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  color: 'text.secondary',
                  bgcolor: 'background.default',
                  borderRadius: 1
                }}>
                  <ShoppingCartIcon sx={{ fontSize: 48 }} />
                  <Typography>
                    Nenhum produto encontrado. Clique em "Buscar Produtos do Sistema" para carregar produtos.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {products
                    .filter(product => !selectedProductCategory || product.category === selectedProductCategory)
                    .map((product) => (
                    <ListItem
                      key={product.id}
                      button
                      onClick={() => setSelectedProduct(product)}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemIcon>
                        {product.type === 'produto' ? <ShoppingCartIcon /> : <CategoryIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" noWrap sx={{ maxWidth: '70%' }}>
                              {product.name}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={`R$ ${product.price.toFixed(2)}`} 
                              color="primary"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {product.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip 
                                size="small" 
                                label={product.type === 'produto' ? 'Produto' : 'Serviço'} 
                                color={product.type === 'produto' ? 'info' : 'secondary'}
                                variant="outlined"
                              />
                              {product.category && (
                                <Chip 
                                  size="small" 
                                  label={product.category} 
                                  variant="outlined"
                                />
                              )}
                              {productsInKnowledgeBase.includes(product.id) && (
                                <Chip 
                                  size="small" 
                                  label="Na Base de Conhecimento" 
                                  color="success"
                                  variant="outlined"
                                  icon={<StorageIcon />}
                                />
                              )}
                            </Box>
                          </>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProduct(product);
                            setProductDialogOpen(true);
                            setPhoneNumberForProduct('');
                          }}
                        >
                          <SendIcon color="primary" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MessageIcon />
                Histórico de Conversas
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Dialog de Detalhes do Arquivo */}
      <Dialog open={openFileDialog} onClose={handleCloseFileDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Detalhes do Arquivo
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {selectedFile.name}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo:
                  </Typography>
                  <Typography variant="body1">
                    {selectedFile.type.split('/')[1].toUpperCase()}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tamanho:
                  </Typography>
                  <Typography variant="body1">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Data de Upload:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedFile.uploadDate)}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={selectedFile.status === 'ready' ? 'Pronto' : selectedFile.status === 'processing' ? 'Processando' : 'Erro'}
                    color={selectedFile.status === 'ready' ? 'success' : selectedFile.status === 'processing' ? 'warning' : 'error'}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Informações de Processamento:
                </Typography>
                
                {selectedFile.status === 'ready' ? (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Este arquivo foi processado com sucesso e está pronto para ser usado pelo bot.
                  </Alert>
                ) : selectedFile.status === 'processing' ? (
                  <>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedFile.progress || 0} 
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                    />
                    <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                      {selectedFile.progress}% concluído
                    </Typography>
                  </>
                ) : (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedFile.error || 'Ocorreu um erro ao processar este arquivo.'}
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Fechar</Button>
          {selectedFile && selectedFile.status === 'error' && (
            <Button 
              color="primary" 
              startIcon={<RefreshIcon />}
              onClick={() => {
                // Lógica para tentar novamente
                handleCloseFileDialog();
              }}
            >
              Tentar Novamente
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Atualização */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmação de Atualização
        </DialogTitle>
        <DialogContent>
          {fileToUpdate && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                A atualização substituirá completamente o conteúdo do arquivo existente.
              </Alert>
              
              <Typography variant="body1" gutterBottom>
                Você está prestes a atualizar o arquivo:
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.paper', 
                borderRadius: 1, 
                border: '1px solid', 
                borderColor: 'divider',
                mb: 2
              }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getFileIcon(fileToUpdate.originalName)}
                  {fileToUpdate.originalName}
                </Typography>
              </Box>
              
              <Typography variant="body1" gutterBottom>
                Com o novo arquivo:
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.paper', 
                borderRadius: 1, 
                border: '1px solid', 
                borderColor: 'divider' 
              }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getFileIcon(fileToUpdate.file.name)}
                  {fileToUpdate.file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tamanho: {formatFileSize(fileToUpdate.file.size)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancelar</Button>
          <Button 
            color="primary" 
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleConfirmUpdate}
          >
            Confirmar Atualização
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Nova Conversa */}
      <Dialog 
        open={openNewConversationDialog} 
        onClose={() => setOpenNewConversationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Nova Conversa
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome do Cliente"
              fullWidth
              value={newConversation.customerName}
              onChange={(e) => setNewConversation({ ...newConversation, customerName: e.target.value })}
              required
            />
            <TextField
              label="Telefone do Cliente"
              fullWidth
              value={newConversation.customerPhone}
              onChange={(e) => setNewConversation({ ...newConversation, customerPhone: e.target.value })}
              placeholder="Ex: 5511999999999"
              helperText="Inclua o código do país (Ex: 55 para Brasil)"
              required
            />
            <TextField
              label="Mensagem Inicial"
              fullWidth
              multiline
              rows={3}
              value={newConversation.initialMessage}
              onChange={(e) => setNewConversation({ ...newConversation, initialMessage: e.target.value })}
              placeholder="Olá! Sou o assistente virtual da imobiliária. Como posso ajudar?"
              helperText="Deixe em branco para usar a mensagem padrão"
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Mensagens Rápidas
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {templates.map(template => (
                  <Chip
                    key={template.id}
                    label={template.name}
                    onClick={() => setNewConversation({ 
                      ...newConversation, 
                      initialMessage: template.content 
                    })}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewConversationDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddNewConversation}
            disabled={!newConversation.customerName || !newConversation.customerPhone}
          >
            Criar Conversa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Resposta */}
      <Dialog open={openResponseDialog} onClose={() => setOpenResponseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Responder à Conversa
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" gutterBottom>
              Digite sua resposta:
            </Typography>
            
            <TextField
              label="Resposta"
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResponseDialog(false)}>Cancelar</Button>
          <Button 
            color="primary" 
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendResponse}
            disabled={isGeneratingConversationResponse}
          >
            {isGeneratingConversationResponse ? <CircularProgress size={20} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Detalhes do Contato */}
      <Dialog open={openContactDialog} onClose={() => setOpenContactDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Detalhes do Contato
        </DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {selectedContact.name}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Telefone:
                  </Typography>
                  <Typography variant="body1">
                    {selectedContact.phoneNumber}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Última Mensagem:
                  </Typography>
                  <Typography variant="body1">
                    {selectedContact.lastMessage}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactDialog(false)}>Fechar</Button>
          <Button 
            color="primary" 
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateLeadFromContact}
            disabled={isCreatingLead}
          >
            {isCreatingLead ? <CircularProgress size={20} /> : 'Criar Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Envio de Produto */}
      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            Enviar Produto via WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Selecione um produto e um contato para enviar informações via WhatsApp.
          </DialogContentText>
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Produto</InputLabel>
            <Select
              value={selectedProduct ? selectedProduct.id : ''}
              onChange={(e) => {
                const product = products.find(p => p.id === e.target.value);
                setSelectedProduct(product || null);
              }}
              label="Produto"
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name} - R$ {product.price.toFixed(2)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Contato</InputLabel>
            <Select
              value={phoneNumberForProduct}
              onChange={(e) => setPhoneNumberForProduct(e.target.value)}
              label="Contato"
            >
              {whatsappContacts.map((contact) => (
                <MenuItem key={contact.id} value={contact.phoneNumber}>
                  {contact.name} ({contact.phoneNumber})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedProduct && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6">{selectedProduct.name}</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedProduct.description}
                </Typography>
                <Typography variant="subtitle1">
                  Preço: R$ {selectedProduct.price.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Categoria: {selectedProduct.category || 'Não categorizado'}
                </Typography>
                <Typography variant="body2">
                  Tipo: {selectedProduct.type === 'produto' ? 'Produto' : 'Serviço'}
                </Typography>
                
                {selectedProduct.imageUrl && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img 
                      src={selectedProduct.imageUrl} 
                      alt={selectedProduct.name}
                      style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="primary"
            disabled={!selectedProduct || !phoneNumberForProduct || isSendingProduct}
            onClick={handleSendProductInfo}
            startIcon={isSendingProduct ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Enviar Produto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Adicionar Produtos à Base de Conhecimento */}
      <Dialog open={openKnowledgeDialog} onClose={() => setOpenKnowledgeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon />
            Adicionar Produtos à Base de Conhecimento
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Selecione os produtos que deseja adicionar à base de conhecimento. Produtos na base de conhecimento permitem que o bot forneça informações mais detalhadas e precisas sobre os produtos quando os clientes fizerem perguntas. Isso melhora significativamente a qualidade das respostas automáticas.
          </DialogContentText>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedProductsForKnowledge.length === products.length}
                  indeterminate={selectedProductsForKnowledge.length > 0 && selectedProductsForKnowledge.length < products.length}
                  onChange={handleToggleSelectAllProducts}
                />
              }
              label="Selecionar Todos"
            />
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por Categoria</InputLabel>
              <Select
                value={selectedProductCategory}
                onChange={(e) => setSelectedProductCategory(e.target.value)}
                label="Filtrar por Categoria"
                size="small"
              >
                <MenuItem value="">Todas as Categorias</MenuItem>
                {productCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Status: {productsInKnowledgeBase.length} produtos na base de conhecimento
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(productsInKnowledgeBase.length / products.length) * 100} 
              sx={{ mt: 1, height: 8, borderRadius: 2 }}
              color="success"
            />
          </Box>
          
          <List sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {products
              .filter(product => !selectedProductCategory || product.category === selectedProductCategory)
              .map((product) => (
              <ListItem
                key={product.id}
                button
                onClick={() => {
                  setSelectedProductsForKnowledge(prev => {
                    if (prev.includes(product.id)) {
                      return prev.filter(id => id !== product.id);
                    } else {
                      return [...prev, product.id];
                    }
                  });
                }}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  bgcolor: productsInKnowledgeBase.includes(product.id) ? 'success.light' : 'background.paper',
                  '&:hover': {
                    bgcolor: productsInKnowledgeBase.includes(product.id) ? 'success.light' : 'action.hover',
                  }
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedProductsForKnowledge.includes(product.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelectedProductsForKnowledge(prev => {
                        if (prev.includes(product.id)) {
                          return prev.filter(id => id !== product.id);
                        } else {
                          return [...prev, product.id];
                        }
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" noWrap sx={{ maxWidth: '70%' }}>
                        {product.name}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={`R$ ${product.price.toFixed(2)}`} 
                        color="primary"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {product.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip 
                          size="small" 
                          label={product.type === 'produto' ? 'Produto' : 'Serviço'} 
                          color={product.type === 'produto' ? 'info' : 'secondary'}
                          variant="outlined"
                        />
                        {product.category && (
                          <Chip 
                            size="small" 
                            label={product.category} 
                            variant="outlined"
                          />
                        )}
                        {productsInKnowledgeBase.includes(product.id) && (
                          <Chip 
                            size="small" 
                            label="Na Base de Conhecimento" 
                            color="success"
                            variant="outlined"
                            icon={<StorageIcon />}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          
          {isProcessingProducts && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" align="center">
                Processando {selectedProductsForKnowledge.length} produtos...
              </Typography>
              <LinearProgress sx={{ mt: 1 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKnowledgeDialog(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="primary"
            disabled={selectedProductsForKnowledge.length === 0 || isProcessingProducts}
            onClick={handleProcessProductsForKnowledgeBase}
            startIcon={isProcessingProducts ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            Adicionar {selectedProductsForKnowledge.length} Produtos
          </Button>
          <Button 
            variant="contained" 
            color="error"
            disabled={selectedProductsForKnowledge.length === 0 || isRemovingFromKnowledge}
            onClick={handleRemoveProductsFromKnowledgeBase}
            startIcon={isRemovingFromKnowledge ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Remover {selectedProductsForKnowledge.length} Produtos
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MessageIcon />
          Chat de Teste
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Digite sua mensagem"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            sx={{ width: '100%' }}
          />

          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendMessage}
            disabled={isGeneratingResponse}
          >
            {isGeneratingResponse ? <CircularProgress size={24} /> : 'Enviar'}
          </Button>
        </Box>

        <Box sx={{ mt: 3, maxHeight: 300, overflowY: 'auto' }}>
          {chatMessages.length === 0 ? (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              color: 'text.secondary'
            }}>
              <WhatsAppIcon sx={{ fontSize: 48 }} />
              <Typography>
                Envie uma mensagem para testar o bot com tecnologia RAG
              </Typography>
            </Box>
          ) : (
            chatMessages.map((message) => (
              <Box key={message.id} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={message.sender === 'user' ? 'Você' : 'Bot'}
                  color={message.sender === 'user' ? 'primary' : 'success'}
                  size="small"
                />
                <Typography variant="body1">
                  {message.text}
                </Typography>
              </Box>
            ))
          )}
          <div ref={chatEndRef} />
        </Box>
      </Box>
    </Box>
  );
};

export default WhatsAppAIPage;
